import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@material-ui/core';
import ExtraServiceOrderForm from '../../../ExtraService/components/ExtraServiceOrderForm';
import { EXTRA_SERVICE_ORDER_ACTION } from '../../../../extraService/datasets/extra_service_order_action';
import { formatMoneyValue } from '../../../../core/utils/useMoneyHelper';
import { DateTime } from 'luxon';
import SlidePanel from '../../../../core/components/SlidePanel';
import { INormalizedExtraService } from '../../../../extraService/types/extraServices';
import useLazyQueryExtraServicesOrders from '../../../../extraService/api/gql/useQueryExtraServicesOrders/useLazyQueryExtraServicesOrders';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from 'core/redux/rootReducer';
import { generateExtraServiceFormInitialData } from '../../../../extraService/utils/useExtraServiceHelper';
import useSubmitExtraServiceOrder from '../../../../extraService/hooks/useSubmitExtraServiceOrder';
import { coreActions } from 'core/redux/actions/coreActions';

interface IExtraServiceOrderSlideIn {
    selectedService: INormalizedExtraService | null;
    open: boolean;
    onClose: () => void;
    onCloseParentSlidePanel?: () => void;
}

export default function ExtraServiceOrderSlideIn({
    selectedService,
    open,
    onClose,
    onCloseParentSlidePanel,
}: IExtraServiceOrderSlideIn): JSX.Element {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { getOrders } = useLazyQueryExtraServicesOrders();
    const selectedAgreementId = useSelector(({ agreements }: AppState) => agreements.selectedAgreement?.id);
    const { submit, loading, error } = useSubmitExtraServiceOrder();
    const handleCloseAll = () => {
        onClose();
        onCloseParentSlidePanel?.();
    };

    return (
        <SlidePanel
            panelId={`dashboard-extra-service-order-create`}
            isOpen={open}
            handleClose={handleCloseAll}
            headerText={t('extraServices.order.title')}
        >
            <>
                {!!selectedService && (
                    <>
                        <Box style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <Typography variant={'h6'}>{selectedService.name}</Typography>
                            <Typography>{selectedService.description}</Typography>
                            <div>{t(`extraServices.order.description.${selectedService.paymentType}`)}</div>
                        </Box>
                        <ExtraServiceOrderForm
                            isConfirmed={false}
                            paymentType={selectedService.paymentType}
                            action={EXTRA_SERVICE_ORDER_ACTION.ORDER}
                            serviceName={selectedService.name}
                            price={formatMoneyValue(selectedService.price, selectedService.currency) || '0'}
                            dateFrom={DateTime.now().toSQLDate()}
                            orderId={selectedService.id}
                            initialFormData={generateExtraServiceFormInitialData(
                                selectedService.id,
                                Number(selectedAgreementId ?? 0),
                            )}
                            loading={loading}
                            error={error}
                            onSubmit={async (formData) => {
                                try {
                                    await submit({
                                        action: EXTRA_SERVICE_ORDER_ACTION.ORDER,
                                        paymentType: selectedService.paymentType,
                                        orderId: selectedService.id,
                                        formData,
                                    });
                                    await getOrders();
                                    dispatch(coreActions.notification.setSuccess(t('notices.isDone')));
                                } catch {
                                    dispatch(coreActions.notification.setError(t('notices.criticalError')));
                                }
                            }}
                            onClose={handleCloseAll}
                        />
                    </>
                )}
            </>
        </SlidePanel>
    );
}
